import { openDB } from 'idb';
import { v4 as uuidv4 } from 'uuid';
import { LOOKUP_TYPE } from '../constants/usageTracking';

const ObjectStore = {
    LOOKUP: 'ut_lookup',
    LOOKUP_PRODUCT: 'ut_lookup_product',
};

const Index = {
    LOOKUPS_BY_VEHICLE: 'lookupsByWorksheetVehicleId',
};

const READ_WRITE = 'readwrite';

let _db;
const getConnection = async () => {
    if (!_db) {
        _db = await openDB('ut', 1, {
            upgrade(db) {
                const lookupStore = db.createObjectStore(ObjectStore.LOOKUP, { keyPath: 'uuid' });
                lookupStore.createIndex(Index.LOOKUPS_BY_VEHICLE, 'vehicleId');

                db.createObjectStore(ObjectStore.LOOKUP_PRODUCT, { keyPath: ['uuid', 'partId'] });
            },
        });
    }

    return _db;
};

export const saveLookups = async ({ utLookup, utLookupProducts }) => {
    const db = await getConnection();
    const tx = db.transaction([ObjectStore.LOOKUP, ObjectStore.LOOKUP_PRODUCT], READ_WRITE);
    const txArray = [];

    // Adding ut_lookups record
    txArray.push(tx.objectStore(ObjectStore.LOOKUP).put(utLookup));

    // Adding ut_lookups_products
    const lookupProductStore = tx.objectStore(ObjectStore.LOOKUP_PRODUCT);
    utLookupProducts.forEach((item) => {
        txArray.push(lookupProductStore.put(item));
    });

    txArray.push(tx.done);
    return Promise.allSettled(txArray);
};

export const generateTimeStamp = () => {
    const dateParts = new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        hour12: false,
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
    }).formatToParts(Date.now());

    return `${dateParts[0].value}, ${dateParts[4].value} ${dateParts[2].value} ${dateParts[6].value} ${dateParts[8].value}:${dateParts[10].value}:${dateParts[12].value} ${dateParts[14].value}`;
};

export const generateUUIDObject = () => ({
    uuid: uuidv4(),
    timeStamp: generateTimeStamp(),
});

export const createIDBObjects = ({ uuid, timeStamp, currentVehicle, catalogLookupData, resultDetails }) => {
    const utLookup = {
        uuid,
        timeStamp,
        lookupType: catalogLookupData.lookupType ?? LOOKUP_TYPE.DRILL_DOWN,
        searchQuery: catalogLookupData.searchQuery,
        vehicleID: currentVehicle?.id,
        vehicleBaseId: currentVehicle?.vehicleId,
        selectedManufacturer: catalogLookupData.selectedManufacturer,
    };

    const utLookupProducts = [];

    const lookupPartTypeIdsByPartTypeId =
        catalogLookupData.selectedPartTypes?.reduce((parentPartTypeIdsByPartTypeId, { partTypeId, resolvedPartTypes }) => {
            const lookupPartTypeIdByResolvedPartTypes =
                resolvedPartTypes?.reduce((response, resolvedPartType) => {
                    response[resolvedPartType.partTypeId] = partTypeId;

                    return response;
                }, {}) ?? {};

            return Object.assign(parentPartTypeIdsByPartTypeId, lookupPartTypeIdByResolvedPartTypes);
        }, {}) ?? {};

    resultDetails.forEach((result) => {
        utLookupProducts.push({
            uuid,
            legacyPartId: `${result.line}|${result.item}`,
            partId: `${result.brandCode}|${result.catalogItemId}`,
            partTypeId: result.partTypeId,
            lookupPartTypeId: lookupPartTypeIdsByPartTypeId[result.partTypeId],
            parentPartTypeId: result.parentPartTypeId,
            localQuantityOnHand: result.localQOH ?? 0,
            stocking: result.stocking,
        });
    });

    return { utLookup, utLookupProducts };
};

export const saveRelatedProductsLookup = async (relatedProducts) => {
    if (relatedProducts.length === 0) return;

    const utLookup = {
        uuid: relatedProducts[0].uuid.uuid,
        timeStamp: relatedProducts[0].uuid.timeStamp,
        lookupType: relatedProducts[0].lookupType,
        vehicleID: relatedProducts[0].vehicleId,
        vehicleBaseId: relatedProducts[0].baseVehicleId,
        searchQuery: null,
        selectedManufacturer: null,
    };

    const utLookupProducts = relatedProducts.map((product) => ({
        uuid: product.uuid.uuid,
        partId: product.partId,
        legacyPartId: product.legacyPartId,
        partTypeId: product.partTypeId,
        parentPartTypeId: product.parentPartTypeId,
        localQuantityOnHand: product.localQuantityOnHand,
        stocking: product.stocking,
    }));

    const response = { utLookup, utLookupProducts };

    await saveLookups(response);

    return response;
};

export const completeCatalogLookupEvent = async (uuid) => {
    const db = await getConnection();
    const tx = db.transaction([ObjectStore.LOOKUP, ObjectStore.LOOKUP_PRODUCT], 'readwrite');

    await Promise.allSettled([
        tx.objectStore(ObjectStore.LOOKUP).delete(uuid),
        (async () => {
            const store = tx.objectStore(ObjectStore.LOOKUP_PRODUCT);
            for await (const cursor of store) {
                if (cursor.value.uuid === uuid) {
                    store.delete(cursor.primaryKey);
                }
            }
        })(),
        tx.done,
    ]);
};

export const getAllUtLookupsAndProduct = async () => {
    const db = await getConnection();
    const utLookup = await db.getAll(ObjectStore.LOOKUP);
    const utLookupProducts = await db.getAll(ObjectStore.LOOKUP_PRODUCT);

    return { utLookup, utLookupProducts };
};

export const clearAll = async () => {
    const db = await getConnection();
    const tx = db.transaction([ObjectStore.LOOKUP, ObjectStore.LOOKUP_PRODUCT], 'readwrite');

    await Promise.allSettled([tx.objectStore(ObjectStore.LOOKUP_PRODUCT).clear(), tx.objectStore(ObjectStore.LOOKUP).clear(), tx.done]);
};
